<!--
@component
Divider

A flexible divider component that can be used to separate content visually.

@prop orientation - The orientation of the divider ('horizontal' or 'vertical').
@prop variant - The visual variant of the divider ('solid', 'dashed', or 'dotted').
@prop thickness - The thickness of the divider line.
@prop length - The length of the divider (100% by default for horizontal, 100% by default for vertical).
@prop color - The color of the divider.
@prop css - Custom CSS styles to be applied to the divider.

@example
<Divider orientation="horizontal" variant="solid" thickness="1px" color="gray.200" />
-->

<script lang="ts">
  import { css } from '@agapi/ui/panda/css';
  import { dividerStyles } from './divider.styles';
  import type { ColorToken, Token } from '@agapi/ui/panda/tokens';
  import { token } from '@agapi/ui/panda/utils';
  import { useActions, type ActionComponentProps } from '../../utils/useAction';
  import type { DividerProps } from './divider.types';

  const {
    color,
    use,
    orientation = 'horizontal',
    variant = 'solid',
    thickness = 1,
    css: cssProp = {}
  }: DividerProps = $props();

  const borderColor = color ? token(('colors.' + color) as Token) : 'currentColor';

  const dividerStyleString = `
    border-style: ${variant};
    border-width: 0px;
    background-color: transparent;
    ${
      orientation === 'horizontal'
        ? `
        border-bottom-width: ${thickness}px;
        border-bottom-color: ${borderColor};
        height: 0;
      `
        : `
        border-left-width: ${thickness}px;
        border-left-color: ${borderColor};
        width: 0;
      `
    }
  `;
</script>

<hr
  use:useActions={use || []}
  aria-orientation={orientation}
  class={css(dividerStyles.raw({ orientation }), cssProp)}
  style={dividerStyleString}
/>
